import { useQuery } from '@tanstack/react-query';
import { Users, Globe, FileText, DollarSign, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getAccessToken } from '../../services/tokenStore';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';

async function fetchAdminStats() {
  const token = getAccessToken();
  const res = await fetch(`${API_URL}/admin/stats`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Failed to fetch stats');
  return res.json();
}

export default function AdminDashboard() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-stats'],
    queryFn: fetchAdminStats,
  });

  const stats = data?.data?.stats || {};
  const recentUsers = data?.data?.recentUsers || [];

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white">Admin Dashboard</h1>
          <p className="text-gray-400 mt-1">Manage your platform</p>
        </div>
        <Link
          to="/admin/generate"
          className="flex items-center gap-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition-colors"
        >
          <Plus size={20} />
          Generate Website
        </Link>
      </div>

      {isLoading ? (
        <div className="text-gray-400">Loading...</div>
      ) : error ? (
        <div className="text-red-400">Error loading stats</div>
      ) : (
        <>
          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <StatCard
              title="Total Users"
              value={stats.users || 0}
              icon={Users}
              color="blue"
            />
            <StatCard
              title="Total Websites"
              value={stats.websites || 0}
              subtitle={`${stats.activeWebsites || 0} active`}
              icon={Globe}
              color="emerald"
            />
            <StatCard
              title="Total Leads"
              value={stats.leads || 0}
              icon={FileText}
              color="purple"
            />
            <StatCard
              title="Subscriptions"
              value={stats.subscriptions || 0}
              icon={DollarSign}
              color="yellow"
            />
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
              <h2 className="text-xl font-semibold text-white mb-4">Quick Actions</h2>
              <div className="grid grid-cols-2 gap-4">
                <Link
                  to="/admin/users"
                  className="p-4 bg-gray-700 hover:bg-gray-600 rounded-lg text-center transition-colors"
                >
                  <Users className="mx-auto mb-2 text-blue-400" size={24} />
                  <span className="text-white">Manage Users</span>
                </Link>
                <Link
                  to="/admin/websites"
                  className="p-4 bg-gray-700 hover:bg-gray-600 rounded-lg text-center transition-colors"
                >
                  <Globe className="mx-auto mb-2 text-emerald-400" size={24} />
                  <span className="text-white">Manage Websites</span>
                </Link>
                <Link
                  to="/admin/generate"
                  className="p-4 bg-gray-700 hover:bg-gray-600 rounded-lg text-center transition-colors"
                >
                  <Plus className="mx-auto mb-2 text-purple-400" size={24} />
                  <span className="text-white">Generate Site</span>
                </Link>
                <Link
                  to="/admin/leads"
                  className="p-4 bg-gray-700 hover:bg-gray-600 rounded-lg text-center transition-colors"
                >
                  <FileText className="mx-auto mb-2 text-yellow-400" size={24} />
                  <span className="text-white">View All Leads</span>
                </Link>
              </div>
            </div>

            {/* Recent Users */}
            <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
              <h2 className="text-xl font-semibold text-white mb-4">Recent Users</h2>
              <div className="space-y-3">
                {recentUsers.length === 0 ? (
                  <p className="text-gray-400">No users yet</p>
                ) : (
                  recentUsers.map((user: any) => (
                    <div
                      key={user.id}
                      className="flex items-center justify-between p-3 bg-gray-700 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center text-white text-sm">
                          {user.name?.charAt(0) || '?'}
                        </div>
                        <div>
                          <p className="text-white">{user.name}</p>
                          <p className="text-gray-400 text-sm">{user.email}</p>
                        </div>
                      </div>
                      <span
                        className={`px-2 py-1 rounded text-xs ${
                          user.role === 'admin'
                            ? 'bg-purple-500/20 text-purple-400'
                            : 'bg-gray-600 text-gray-300'
                        }`}
                      >
                        {user.role}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function StatCard({
  title,
  value,
  subtitle,
  icon: Icon,
  color,
}: {
  title: string;
  value: number;
  subtitle?: string;
  icon: any;
  color: string;
}) {
  const colorClasses: Record<string, string> = {
    blue: 'bg-blue-500/20 text-blue-400',
    emerald: 'bg-emerald-500/20 text-emerald-400',
    purple: 'bg-purple-500/20 text-purple-400',
    yellow: 'bg-yellow-500/20 text-yellow-400',
  };

  return (
    <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
      <div className="flex items-center justify-between mb-4">
        <span className="text-gray-400">{title}</span>
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          <Icon size={20} />
        </div>
      </div>
      <p className="text-3xl font-bold text-white">{value}</p>
      {subtitle && <p className="text-gray-400 text-sm mt-1">{subtitle}</p>}
    </div>
  );
}
